import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Button,
	Typography,
} from '@mui/material';

interface AutoLogoutProps {
	timeoutSeconds: number;
	warningDurationSeconds: number;
	onLogout: () => void;
}

const AutoLogout: React.FC<AutoLogoutProps> = ({
	timeoutSeconds,
	warningDurationSeconds,
	onLogout,
}) => {
	const warnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const [showWarning, setShowWarning] = useState(false);
	const [countdown, setCountdown] = useState<number>(warningDurationSeconds);

	const clearAllTimers = useCallback(() => {
		if (warnTimerRef.current) {
			clearTimeout(warnTimerRef.current);
			warnTimerRef.current = null;
		}
		if (logoutTimerRef.current) {
			clearTimeout(logoutTimerRef.current);
			logoutTimerRef.current = null;
		}
		if (countdownIntervalRef.current) {
			clearInterval(countdownIntervalRef.current);
			countdownIntervalRef.current = null;
		}
	}, []);

	const startCountdown = useCallback((seconds: number) => {
		setCountdown(seconds);
		// clear any previous interval just in case
		if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
		countdownIntervalRef.current = setInterval(() => {
			setCountdown(prev => {
				if (prev <= 1) {
					if (countdownIntervalRef.current) {
						clearInterval(countdownIntervalRef.current);
						countdownIntervalRef.current = null;
					}
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, []);

	const scheduleTimers = useCallback(() => {
		clearAllTimers();
		setShowWarning(false);

		// Guard: if warning >= total, show warning immediately with total countdown
		const warnDelay = Math.max(0, timeoutSeconds - warningDurationSeconds);
		const actualCountdown = Math.min(warningDurationSeconds, timeoutSeconds);

		// Schedule warning
		warnTimerRef.current = setTimeout(() => {
			setShowWarning(true);
			startCountdown(actualCountdown);
		}, warnDelay * 1000);

		// Schedule logout at total timeout
		logoutTimerRef.current = setTimeout(() => {
			if (countdownIntervalRef.current) {
				clearInterval(countdownIntervalRef.current);
				countdownIntervalRef.current = null;
			}
			onLogout();
		}, timeoutSeconds * 1000);
	}, [clearAllTimers, onLogout, startCountdown, timeoutSeconds, warningDurationSeconds]);

	const handleActivity = useCallback(() => {
		// Any user activity resets the idle timers
		scheduleTimers();
	}, [scheduleTimers]);

	const handleCancelLogout = useCallback(() => {
		// Treat as activity
		handleActivity();
	}, [handleActivity]);

	useEffect(() => {
		// Start timers on mount & whenever timeouts change
		scheduleTimers();

		const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
		events.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));

		return () => {
			events.forEach(evt => window.removeEventListener(evt, handleActivity as EventListener));
			clearAllTimers();
		};
	}, [handleActivity, scheduleTimers, clearAllTimers]);

	return (
		<Dialog open={showWarning} onClose={handleCancelLogout}>
			<DialogTitle>Auto Logout Warning</DialogTitle>
			<DialogContent>
				<Typography>
					You have been inactive. You will be logged out in <strong>{countdown} seconds</strong>.
				</Typography>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCancelLogout} color="primary" variant="contained">
					Stay Signed In
				</Button>
				<Button onClick={onLogout} color="secondary" variant="contained">
					Logout Now
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AutoLogout;
